/**
 * Pre-built venue seating templates for nautical canvas shapes.
 * Coordinates are normalized to a 1000x700 reference canvas — the
 * applyCanvasTemplate helper rescales to the user's current canvas size.
 *
 * Each template returns a list of "section-like" elements ready for the editor:
 *   { type, name, color, capacity, shape, style, rotation, position{x,y,w,h} }
 */

const REF_W = 1000;
const REF_H = 700;

const scaleTo = (elements, canvasWidth, canvasHeight) => {
  const sx = canvasWidth / REF_W;
  const sy = canvasHeight / REF_H;
  return elements.map((el, i) => ({
    id: `el_${Date.now()}_${i}`,
    type: el.type,
    name: el.name,
    color: el.color,
    capacity: el.capacity || 0,
    price: null,
    tier_id: null,
    section_type: 'general',
    shape: el.shape || 'rect',
    style: el.style || 'modern',
    rotation: el.rotation || 0,
    position: {
      x: Math.round(el.x * sx),
      y: Math.round(el.y * sy),
      width: Math.round(el.w * sx),
      height: Math.round(el.h * sy),
    },
    locked: false,
    visible: true,
    zIndex: i,
  }));
};

/**
 * Catamaran top-down layout:
 *  - Bar at the bow (between the two hulls)
 *  - Cabina DJ central
 *  - Pista (general) center deck
 *  - Lounge VIP at each stern corner
 *  - Baño + Entrada de embarque
 */
const CATAMARAN_ELEMENTS = [
  { type: 'entrance', name: 'Embarque',     color: '#3B82F6', shape: 'rect',   x: 120,  y: 250, w: 70,  h: 60,  style: 'minimalist' },
  { type: 'entrance', name: 'Embarque 2',   color: '#3B82F6', shape: 'rect',   x: 810,  y: 250, w: 70,  h: 60,  style: 'minimalist' },
  { type: 'bar',      name: 'Barra Proa',   color: '#3B82F6', shape: 'rect',   x: 320,  y: 180, w: 360, h: 70,  capacity: 0,   style: 'modern' },
  { type: 'djbooth',  name: 'Cabina DJ',    color: '#6366F1', shape: 'rect',   x: 400,  y: 290, w: 200, h: 90,                  style: 'futuristic' },
  { type: 'section',  name: 'Pista',        color: '#D4AF37', shape: 'rounded',x: 230,  y: 400, w: 540, h: 130, capacity: 120, style: 'modern' },
  { type: 'lounge',   name: 'Lounge Babor', color: '#D4AF37', shape: 'rounded',x: 110,  y: 470, w: 130, h: 170, capacity: 20,  style: 'luxury' },
  { type: 'lounge',   name: 'Lounge Estribor', color: '#D4AF37', shape: 'rounded', x: 760, y: 470, w: 130, h: 170, capacity: 20, style: 'luxury' },
  { type: 'bathroom', name: 'Baño',         color: '#06B6D4', shape: 'rect',   x: 360,  y: 590, w: 70,  h: 50,                  style: 'minimalist' },
  { type: 'bathroom', name: 'Baño 2',       color: '#06B6D4', shape: 'rect',   x: 570,  y: 590, w: 70,  h: 50,                  style: 'minimalist' },
];

/**
 * Yacht / monohull layout — single bow.
 */
const YACHT_ELEMENTS = [
  { type: 'entrance', name: 'Embarque',     color: '#3B82F6', shape: 'rect',   x: 470,  y: 50,  w: 80,  h: 50,  style: 'minimalist' },
  { type: 'djbooth',  name: 'Cabina DJ',    color: '#6366F1', shape: 'rect',   x: 410,  y: 140, w: 180, h: 80,                  style: 'futuristic' },
  { type: 'bar',      name: 'Barra Central', color: '#3B82F6', shape: 'rounded', x: 350, y: 250, w: 300, h: 70, capacity: 0,    style: 'modern' },
  { type: 'section',  name: 'Pista',        color: '#D4AF37', shape: 'rounded',x: 250,  y: 360, w: 500, h: 130, capacity: 100, style: 'modern' },
  { type: 'lounge',   name: 'Lounge VIP Babor',    color: '#D4AF37', shape: 'rounded', x: 120, y: 410, w: 130, h: 180, capacity: 18, style: 'luxury' },
  { type: 'lounge',   name: 'Lounge VIP Estribor', color: '#D4AF37', shape: 'rounded', x: 750, y: 410, w: 130, h: 180, capacity: 18, style: 'luxury' },
  { type: 'bathroom', name: 'Baño',         color: '#06B6D4', shape: 'rect',   x: 470,  y: 600, w: 70,  h: 50,                  style: 'minimalist' },
];

/**
 * Lancha / ferry — rectangular with wide front entrance.
 */
const BOAT_ELEMENTS = [
  { type: 'entrance', name: 'Embarque',     color: '#3B82F6', shape: 'rect',   x: 460,  y: 30,  w: 80,  h: 50,  style: 'minimalist' },
  { type: 'bar',      name: 'Barra',        color: '#3B82F6', shape: 'rect',   x: 130,  y: 120, w: 280, h: 70,  capacity: 0,   style: 'modern' },
  { type: 'djbooth',  name: 'Cabina DJ',    color: '#6366F1', shape: 'rect',   x: 590,  y: 120, w: 280, h: 80,                  style: 'futuristic' },
  { type: 'section',  name: 'Pista',        color: '#D4AF37', shape: 'rounded',x: 180,  y: 250, w: 640, h: 160, capacity: 140, style: 'modern' },
  { type: 'lounge',   name: 'Lounge Babor', color: '#D4AF37', shape: 'rounded',x: 110,  y: 450, w: 360, h: 150, capacity: 30,  style: 'luxury' },
  { type: 'lounge',   name: 'Lounge Estribor', color: '#D4AF37', shape: 'rounded', x: 530, y: 450, w: 360, h: 150, capacity: 30, style: 'luxury' },
  { type: 'bathroom', name: 'Baño',         color: '#06B6D4', shape: 'rect',   x: 460,  y: 620, w: 80,  h: 50,                  style: 'minimalist' },
];

const TEMPLATES = {
  catamaran: CATAMARAN_ELEMENTS,
  yacht: YACHT_ELEMENTS,
  boat: BOAT_ELEMENTS,
};

export const hasTemplate = (shape) => Object.prototype.hasOwnProperty.call(TEMPLATES, shape);

export const getTemplateElements = (shape, canvasWidth, canvasHeight, floorId, globalStyle) => {
  const tpl = TEMPLATES[shape];
  if (!tpl) return [];
  const scaled = scaleTo(tpl, canvasWidth, canvasHeight);
  return scaled.map(el => ({
    ...el,
    style: el.type === 'entrance' || el.type === 'bathroom' ? 'minimalist' : (globalStyle || el.style),
    floor_id: floorId,
  }));
};

export const TEMPLATE_LABELS = {
  catamaran: 'Plantilla Catamarán (barra + DJ + lounges)',
  yacht: 'Plantilla Yate (cubierta + VIP popa)',
  boat: 'Plantilla Lancha (deck doble)',
};
